/**
 * @genesis_domain Auth Engine
 * @genesis_version V9.1
 * @genesis_intent Login form view model — encapsulates Zod schema, React Hook Form setup, and submission handlers for standard email/password login
 * @genesis_boundaries No UI rendering, no routing logic, no DOM queries
 * @genesis_ai_trail V9.0: Extracted from Login.tsx monolith during tactical DDD refactor.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { useAuth } from '@/context/AuthContext';
import { sendResetPasswordEmail } from '@/domains/auth/services/authService';
import { AuthLoginSchema, type AuthLogin } from '@shared/schemas';
import { toast } from 'sonner';

type LoginFormData = AuthLogin;

export function useLoginViewModel() {
    const { login, isInitializing, error } = useAuth();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormData>({
        resolver: zodResolver(AuthLoginSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const processLogin = async (data: LoginFormData) => {
        try {
            await login(data.email, data.password);
        } catch {
            // Error is handled by AuthContext
        }
    };

    const handleForgotPassword = async (email: string) => {
        try {
            await sendResetPasswordEmail(email);
            return { success: true as const };
        } catch (err) {
            return {
                success: false as const,
                error: err instanceof Error ? err.message : 'Failed to send reset email.',
            };
        }
    };

    const handleGoogleLogin = () => {
        // TODO: Implement Google OAuth
    };

    const handleGithubLogin = () => {
        // TODO: Implement GitHub OAuth
    };

    return {
        register,
        errors,
        isSubmitting,
        isInitializing,
        authError: error,
        onSubmit: handleSubmit(processLogin, () => toast.error('Please check the form for validation errors.')),
        handleForgotPassword,
        handleGoogleLogin,
        handleGithubLogin,
    };
}
